// Seat-pricing math, kept in lockstep with the backend source of truth in
// lib/fontawesome/plans/plans.ex. Centralizes what used to be duplicated
// across PlanBuyView / mixins / priceUtils / AddSeatsView — the duplicates
// drifted (one of them used a flat formula even for FA-TEAM), causing the
// buy page's preset prices to disagree with its own order summary.
//
// Two regimes:
//   - FA-TEAM (annual): graduated tiers. Seats 1–3 included in base,
//     4–10 are $20/yr each, 11–20 are $15/yr each, 21+ are $10/yr each.
//   - FA-TEAM-MONTHLY and any other plan with `seat_upgrade_enabled`:
//     flat per-seat price (`plan.seatUpgradePrice` × extra seats).
//
// Reference totals for QA (Team annual, all seats including base 3):
//   10 seats → $332    25 seats → $532    50 seats → $782    250 seats → $2,782

import { get } from 'lodash'

// Maximum seats served by self-serve. Above this, the customer is routed
// to Enterprise contact-sales. Mirrors `SEAT_MAX` in addOnResources.js and
// the enforcement in the backend AddSeatsController.
export const TEAM_SEAT_MAXIMUM = 250

// FA-TEAM annual graduated schedule. Mirrors the @team_seat_tier_* module
// attributes in lib/fontawesome/plans/plans.ex. `_MAX_UPGRADES` are
// *cumulative* upgrade-seat counts (i.e., counts of seats above the base 3).
export const TEAM_ANNUAL_TIER_1_MAX_UPGRADES = 7 //   seats  4–10
export const TEAM_ANNUAL_TIER_2_MAX_UPGRADES = 17 //  seats 11–20 (cumulative)
export const TEAM_ANNUAL_TIER_1_PRICE_CENTS = 2000 // $20/yr
export const TEAM_ANNUAL_TIER_2_PRICE_CENTS = 1500 // $15/yr
export const TEAM_ANNUAL_TIER_3_PRICE_CENTS = 1000 // $10/yr

// Splits `upgradeSeats` into per-tier seat counts on the FA-TEAM annual
// graduated schedule. Returns the counts only (not subtotals) — callers
// multiply by the per-tier rate constants. Single source of truth used
// by both the total helper and the breakdown helper below.
function splitTeamTiers(upgradeSeats) {
  if (!upgradeSeats || upgradeSeats <= 0) return { tier1: 0, tier2: 0, tier3: 0 }

  const tier1 = Math.min(upgradeSeats, TEAM_ANNUAL_TIER_1_MAX_UPGRADES)
  const tier2 = Math.min(Math.max(upgradeSeats - TEAM_ANNUAL_TIER_1_MAX_UPGRADES, 0), TEAM_ANNUAL_TIER_2_MAX_UPGRADES - TEAM_ANNUAL_TIER_1_MAX_UPGRADES)
  const tier3 = Math.max(upgradeSeats - TEAM_ANNUAL_TIER_2_MAX_UPGRADES, 0)

  return { tier1, tier2, tier3 }
}

// Total *additional* cost in cents for `upgradeSeats` extra seats on the
// FA-TEAM annual graduated schedule. Excludes the base plan price.
export function graduatedTeamSeatTotalCents(upgradeSeats) {
  const { tier1, tier2, tier3 } = splitTeamTiers(upgradeSeats)

  return tier1 * TEAM_ANNUAL_TIER_1_PRICE_CENTS + tier2 * TEAM_ANNUAL_TIER_2_PRICE_CENTS + tier3 * TEAM_ANNUAL_TIER_3_PRICE_CENTS
}

// Total additional cost in cents for `upgradeSeats` extra seats on `plan`.
// SKU-aware: FA-TEAM uses graduated; any other plan with seat upgrades
// enabled uses flat (`seatUpgradePrice.amount` × floor(seats / interval)).
// Returns 0 for plans without seat upgrades enabled (Solo / Starter).
export function extraSeatTotalCents(plan, upgradeSeats) {
  if (!plan || !upgradeSeats || upgradeSeats <= 0) return 0

  if (plan.sku === 'FA-TEAM') {
    return graduatedTeamSeatTotalCents(upgradeSeats)
  }

  if (!get(plan, 'seatUpgradeEnabled')) return 0

  const unitPrice = get(plan, ['seatUpgradePrice', 'amount'], 0)
  const interval = get(plan, 'seatUpgradeInterval', 1) || 1
  const multiple = Math.floor(upgradeSeats / interval)
  return unitPrice * multiple
}

// Total cost in cents for `totalSeats` seats on `plan` (base + extras).
// `totalSeats` is the customer-facing count (e.g., 10 for "I want 10 seats"),
// inclusive of the seats included in the base plan price.
export function planTotalCents(plan, totalSeats) {
  if (!plan) return 0
  const basePrice = get(plan, ['price', 'amount'], 0)
  const baseSeats = get(plan, 'seats', 0) || 0
  const requested = Number.isFinite(totalSeats) ? totalSeats : baseSeats
  const upgradeSeats = Math.max(requested - baseSeats, 0)
  return basePrice + extraSeatTotalCents(plan, upgradeSeats)
}

// Per-tier breakdown of the extra-seat cost for `upgradeSeats` seats on
// `plan`. Used by the order summary to show the user the per-tier math.
// For FA-TEAM, returns up to three rows (one per non-empty graduated
// tier); for flat-pricing plans, returns one row.
export function extraSeatBreakdown(plan, upgradeSeats) {
  if (!plan || !upgradeSeats || upgradeSeats <= 0) return []

  const baseSeats = get(plan, 'seats', 0) || 0

  if (plan.sku === 'FA-TEAM') {
    const { tier1, tier2, tier3 } = splitTeamTiers(upgradeSeats)

    // Each row spans only the seats that actually fall in its tier, so a
    // 13-seat order reads "Seats 11–13" (3 in tier 2), not the full
    // "Seats 11–20" boundary.
    const segments = [
      { seats: tier1, rateCents: TEAM_ANNUAL_TIER_1_PRICE_CENTS },
      { seats: tier2, rateCents: TEAM_ANNUAL_TIER_2_PRICE_CENTS },
      { seats: tier3, rateCents: TEAM_ANNUAL_TIER_3_PRICE_CENTS }
    ]

    const rows = []
    let lastSeat = baseSeats
    segments.forEach(({ seats, rateCents }) => {
      if (seats <= 0) return
      const fromSeat = lastSeat + 1
      const toSeat = lastSeat + seats
      rows.push({
        label: seats === 1 ? `Seat ${fromSeat}` : `Seats ${fromSeat}–${toSeat}`,
        seats,
        rateCents,
        subtotalCents: seats * rateCents
      })
      lastSeat = toSeat
    })
    return rows
  }

  if (!get(plan, 'seatUpgradeEnabled')) return []

  // Match `extraSeatTotalCents` interval handling: flat-pricing plans
  // can charge per block of N seats (`seatUpgradeInterval`), so the
  // breakdown row reports the actual seats charged (floor to interval)
  // and an aligned subtotal. No current plan uses interval > 1, but
  // without this the breakdown rows could disagree with the order
  // total if a future plan does.
  const unitPrice = get(plan, ['seatUpgradePrice', 'amount'], 0)
  const interval = get(plan, 'seatUpgradeInterval', 1) || 1
  const multiple = Math.floor(upgradeSeats / interval)

  if (multiple <= 0) return []

  return [
    {
      label: 'Additional seats',
      seats: multiple * interval,
      rateCents: unitPrice,
      subtotalCents: unitPrice * multiple
    }
  ]
}

// What the FIRST extra seat costs on `plan`, for a surface that quotes one
// price rather than a whole breakdown. `isGraduated` says the rate drops at
// later tier boundaries, so that surface has to qualify it ("from $20 per
// seat") instead of stating it flat. Null when the plan sells no extra seats.
export function extraSeatEntryRate(plan) {
  const [first] = extraSeatBreakdown(plan, 1)
  if (!first) return null

  return { rateCents: first.rateCents, isGraduated: plan.sku === 'FA-TEAM' }
}

// Per-seat tier rate (cents) shown on a preset button for the annual
// Team graduated schedule. Preset counts (3, 10, 20, 50) are tied to
// tier boundaries; this returns the rate of the tier that ENDS at the
// preset count, so the button reads "+$20/seat" for the 10-seat preset
// (since seats 4–10 are tier 1), "+$15/seat" for 20, etc. Returns 0
// for the base tier (3 seats included).
export function tierRateForPresetCents(presetSeats) {
  if (presetSeats <= 3) return 0
  if (presetSeats - 3 <= TEAM_ANNUAL_TIER_1_MAX_UPGRADES) return TEAM_ANNUAL_TIER_1_PRICE_CENTS
  if (presetSeats - 3 <= TEAM_ANNUAL_TIER_2_MAX_UPGRADES) return TEAM_ANNUAL_TIER_2_PRICE_CENTS
  return TEAM_ANNUAL_TIER_3_PRICE_CENTS
}
